import React, { useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Route, Link, useLocation } from 'react-router-dom';

import { Card, Button, Snackbar, Alert } from '@mui/material';

import { AppContext } from '@/AppContext';
import { shortenDate } from '../../../MeetupList/components/MeetupsTable/MeetupsTable';
import { ServicePlanActionType } from '@/api';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import BuyOptionsPack from '../BuyOptionsPack/BuyOptionsPack';
import BuyPackError from '../BuyPackError/BuyPackError';
import ChangeTariffBasic from '../ChangeTariffBasic/ChangeTariffBasic';
import ChangeTariffStandart from '../ChangeTariffStandart/ChangeTariffStandart';
import ChangeTariffUltimate from '../ChangeTariffUltimate/ChangeTariffUltimate';
import Tariff from '../Tariff/Tariff';

import NfcIcon from '@mui/icons-material/Nfc';
import DomainIcon from '@mui/icons-material/Domain';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';

const PREFIX = 'ProfileAccountView';

const classes = {
  root: `${PREFIX}-root`,
  card: `${PREFIX}-card`,
  card__header: `${PREFIX}-card__header`,
  card__content: `${PREFIX}-card__content`,
  price: `${PREFIX}-price`,
  description: `${PREFIX}-description`,
  cardButton: `${PREFIX}-cardButton`,
  td: `${PREFIX}-td`,
  note: `${PREFIX}-note`,
  link: `${PREFIX}-link`,
  tarifsContainer: `${PREFIX}-tarifsContainer`,
  tariffIcon: `${PREFIX}-tariffIcon`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    fontFamily: 'Roboto, sans-serif',
    marginTop: 82,
  },

  [`& .${classes.card}`]: {
    marginBottom: 82,
    color: '#37474F',
    padding: '0 48px 25px 48px',
    flex: '1 0 40%',
    boxShadow: '0px 5px 6px rgba(0, 0, 0, 0.2), 0px 3px 16px rgba(0, 0, 0, 0.12), 0px 9px 12px rgba(0, 0, 0, 0.14)',

    '&:nth-child(2n + 1)': {
      marginRight: 112,
    },

    '&_tarif': {
      flex: '1 0 100%',
    },
  },

  [`& .${classes.card__header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    lineHeight: '66px',
    fontSize: 20,
    borderBottom: '3px solid #EEEEEE',
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  [`& .${classes.card__content}`]: {
    marginTop: 20,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },

  [`& .${classes.price}`]: {
    fontSize: 20,
    marginBottom: 20,
  },

  [`& .${classes.description}`]: {
    fontSize: 14,
    marginBottom: 33,
    lineHeight: '24px',
  },

  [`& .${classes.cardButton}`]: {
    alignSelf: 'flex-end',
    width: 235,
  },

  [`& .${classes.td}`]: {
    padding: '5px 0px',
    fontSize: 16,
  },

  [`& .${classes.note}`]: {
    fontSize: 12,
    marginTop: 10,
  },

  [`& .${classes.link}`]: {
    color: 'rgba(232, 60, 83, 0.7)',
    fontSize: 14,
    textDecoration: 'underline',
    cursor: 'pointer',
  },

  [`& .${classes.tarifsContainer}`]: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    padding: '30px 0',
    gap: 30,
  },

  [`& .${classes.tariffIcon}`]: {
    color: 'rgba(232, 60, 83, 0.7)',
    height: 25,
    width: 25,
  },
}));

const ProfileAccountView = props => {
  const { className, data, tariffs, setLogFilter, ...rest } = props;
  const servicePacks = {};
  tariffs.servicePacks.forEach(pack => {
    servicePacks[pack.name] = pack;
  });

  const tariffsInfo = [
    {
      id: 'basic',
      name: tariffs.servicePlans[1].publicName,
      icon: <NfcIcon className={classes.tariffIcon} />,
      price: tariffs.servicePlans[1].price + ' руб. + оплата стоек',
      description: ['1 мероприятие в месяц', '200 участников'],
      features: ['Онлайн и офлайн регистрация', 'Чат-бот', 'Редактор бейджей', 'Контроль доступа в залы', 'Статистика'],
    },
    {
      id: 'ultimate',
      name: tariffs.servicePlans[3].publicName,
      icon: <DomainIcon className={classes.tariffIcon} />,
      price: tariffs.servicePlans[3].price + ' руб./месяц + оплата стоек',
      description: ['безлимит на мероприятия и кол-во участников'],
      features: ['Тариф включает в себя весь набор опций из Базового тарифа'],
    },
    {
      id: 'unlimited',
      name: tariffs.servicePlans[4].publicName,
      icon: <AllInclusiveIcon className={classes.tariffIcon} />,
      price: 'цена по запросу',
      description: ['безлимит на мероприятия и кол-во участников'],
      features: [
        'White label - ваше название и логотип',
        'On-premise установка на ваши сервера',
        'Возможность доработок под ваше мероприятие',
      ],
    },
  ];
  const appContext = useContext(AppContext);
  const location = useLocation();

  const goBack = () => {
    appContext.history.push('/profile/account');
  };
  const goToLogs = e => {
    e.preventDefault();
    setLogFilter('payments');
    appContext.history.push('/profile/log');
  };

  const searchParams = new URLSearchParams(location.search);
  const paymentResult = searchParams.get('payment');

  const [snackOpen, setSnackOpen] = useState(!!paymentResult);

  const limitTitle = {
    meetups: 'Мероприятия',
    visitors: 'Посетители',
    sms: 'Рассылки писем',
    deskPerDay: 'Стойка регистрации *',
  };

  return (
    <Root className={classes.root}>
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={paymentResult === 'success' ? 'success' : 'error'}
          variant='filled'
        >
          {paymentResult === 'success'
            ? 'Оплата прошла успешно'
            : 'Оплата не была завершена. Попробуйте снова.'}
        </Alert>
      </Snackbar>
      <Card className={classes.card}>
        <h1 className={classes.card__header}>
          <div>Текущие лимиты</div>
          <a className={classes.link} onClick={goToLogs}>
            История платежей
          </a>
        </h1>
        <div className={classes.card__content}>
          <table style={{ width: '100%' }}>
            <tbody>
              {data.account.limits.map(limit => (
                <tr key={limit.limit}>
                  <td className={classes.td}>{limitTitle[limit.limit]}</td>
                  <td className={classes.td}>
                    <span style={{ fontWeight: 'bold' }}>{limit.current}</span>/{limit.max}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={classes.note}>* - докупается отдельно</div>
        </div>
      </Card>
      <Card className={classes.card}>
        <h1 className={classes.card__header}>Купить пакеты</h1>
        <div className={classes.card__content}>
          <table style={{ width: '100%' }}>
            <tbody>
              {tariffs.servicePacks.map(pack => (
                <tr key={pack.name}>
                  <td className={classes.td}>{pack.description}</td>
                  <td className={classes.td}>
                    <span style={{ fontWeight: 'bold' }}>{pack.price} руб</span>
                  </td>
                  <td className={classes.td}>
                    <Link
                      style={{ fontSize: 16 }}
                      className={classes.link}
                      to={'/profile/account/buy-pack/' + pack.name}
                    >
                      Купить
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <Card className={classes.card} style={{ flex: '1 0 100%' }} id='tariffs'>
        <h1 className={classes.card__header}>Тарифы</h1>
        <div className={classes.tarifsContainer}>
          {tariffsInfo.map(t => (
            <Tariff
              key={t.id}
              data={t}
              active={data.account.servicePlanId.slice(0, data.account.servicePlanId.indexOf('_')) === t.id}
            />
          ))}
        </div>
      </Card>
      <Route path='/profile/account/buy-pack/:name'>
        <BuyOptionsPack packs={servicePacks} accountId={data.account.accountId} />
      </Route>
      <Route path='/profile/account/buy-pack-error/:name'>
        <BuyPackError />
      </Route>
      <Route path='/profile/account/change-tariff/basic'>
        <ChangeTariffBasic accountId={data.account.accountId} />
      </Route>
      <Route path='/profile/account/change-tariff/standart'>
        <ChangeTariffStandart
          accountId={data.account.accountId}
          price={tariffs.servicePlans[2].price}
        />
      </Route>
      <Route path='/profile/account/change-tariff/ultimate'>
        <ChangeTariffUltimate accountId={data.account.accountId} userId={data.account.owner.userId} />
      </Route>
    </Root>
  );
};

export default ProfileAccountView;
